import { useEffect, useState } from 'react';
import clsx from 'clsx';

interface ConnectedRow { expired: boolean; expires_at: string; scope?: string; open_id?: string }
interface OAuthStatus {
  env:       { youtube: boolean; tiktok: boolean };
  connected: { youtube: ConnectedRow | null; tiktok: ConnectedRow | null };
}
interface X402Status {
  enabled: boolean;
  network: string;
  asset: string;
  amount: string;
  wallet: string | null;
  facilitator: string;
  operator_bypass_configured: boolean;
}

const STREAMCLIP_BASE = (import.meta.env.VITE_STREAMCLIP_BASE as string | undefined) ?? 'http://127.0.0.1:3070';

function formatUsdc(rawSmallestUnits: string): string {
  const n = Number(rawSmallestUnits);
  if (!isFinite(n)) return `${rawSmallestUnits} micro-USDC`;
  return `$${(n / 1_000_000).toFixed(2)}`;
}

export function OAuthPanel() {
  const [status, setStatus] = useState<OAuthStatus | null>(null);
  const [x402, setX402] = useState<X402Status | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      const [a, b] = await Promise.all([
        fetch('/api/oauth/status').then((r) => (r.ok ? r.json() : null)),
        fetch('/api/x402/status').then((r) => (r.ok ? r.json() : null))
      ]);
      if (a) setStatus(a);
      if (b) setX402(b);
    } catch { /* skip */ }
  }

  useEffect(() => {
    void refresh();
    const t = window.setInterval(refresh, 8000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    const u = new URL(window.location.href);
    if (u.searchParams.get('oauth')) {
      u.searchParams.delete('oauth'); u.searchParams.delete('ok');
      u.searchParams.delete('expires_at'); u.searchParams.delete('open_id');
      window.history.replaceState({}, '', u.toString());
      void refresh();
    }
  }, []);

  function connect(platform: 'youtube' | 'tiktok') {
    setBusy(true);
    window.location.href = `${STREAMCLIP_BASE}/api/oauth/${platform}/start`;
  }

  if (!status) return null;

  const oauthRow = (label: string, env: boolean, conn: ConnectedRow | null, platform: 'youtube' | 'tiktok') => (
    <div className="flex items-center justify-between gap-3 py-1">
      <div className="flex items-center gap-2">
        <span className={clsx(
          'inline-block w-1.5 h-1.5 rounded-full',
          conn && !conn.expired ? 'bg-signal-on' : conn ? 'bg-danger-500' : 'bg-ink-700'
        )} />
        <span className="text-xs text-ink-300">{label}</span>
        {!env && <span className="text-[10px] text-danger-500">env missing</span>}
        {conn && !conn.expired && <span className="text-[10px] text-ink-500">connected</span>}
        {conn && conn.expired && <span className="text-[10px] text-danger-500">expired</span>}
      </div>
      <button
        onClick={() => connect(platform)}
        disabled={!env || busy}
        className="text-[11px] px-2 py-0.5 rounded-md bg-ink-800 hover:bg-ink-700 text-ink-200 disabled:opacity-40"
      >
        {conn ? 're-auth' : 'connect'}
      </button>
    </div>
  );

  return (
    <div className="flex items-stretch gap-3">
      <div className="rounded-xl border border-ink-800 bg-ink-900/60 px-3 py-2 min-w-[230px]">
        <div className="text-[10px] uppercase tracking-[0.14em] text-ink-500 mb-1">publish targets</div>
        {oauthRow('youtube', status.env.youtube, status.connected.youtube, 'youtube')}
        {oauthRow('tiktok',  status.env.tiktok,  status.connected.tiktok,  'tiktok')}
      </div>
      {x402 && (
        <div className="rounded-xl border border-ink-800 bg-ink-900/60 px-3 py-2 min-w-[230px]">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] uppercase tracking-[0.14em] text-ink-500">x402 gate</span>
            <span className={clsx(
              'inline-block w-1.5 h-1.5 rounded-full',
              x402.enabled ? 'bg-signal-on' : 'bg-ink-700'
            )} />
          </div>
          {x402.enabled ? (
            <>
              <div className="text-xs text-ink-200 font-mono">{formatUsdc(x402.amount)} <span className="text-ink-500">/clip</span></div>
              <div className="text-[10px] text-ink-500 mt-0.5">
                {x402.network} · {x402.wallet}
              </div>
              <div className="text-[10px] text-ink-600 mt-0.5">
                {x402.operator_bypass_configured ? 'operator bypass: on' : 'no operator bypass'}
              </div>
            </>
          ) : (
            <div className="text-[10px] text-danger-500">
              X402_RECEIVING_WALLET unset — uploads free
            </div>
          )}
        </div>
      )}
    </div>
  );
}
